// Cutting tracker — one card per job that has any spools. The
// badge tone reflects how far through cutting the job is. Mockup
// equivalent at production-schedule.html line 876.
import type { JobDto } from "../../../../../../shared/types/production-schedule";

interface Props {
  jobs: JobDto[];
  onOpenJob(id: number): void;
}

const SPOOL_DOT: Record<string, string> = {
  pending: "#9CA09F",
  cutting: "#C77700",
  cut: "#0284C7",
  welding: "#C77700",
  complete: "#2E7D32",
};

function badgeTone(done: number, total: number) {
  if (total === 0)
    return { bg: "rgba(112,115,114,0.14)", color: "#707372", label: "No spools" };
  if (done === total)
    return { bg: "rgba(46,125,50,0.10)", color: "#246628", label: "All cut" };
  if (done > 0)
    return { bg: "rgba(199,119,0,0.12)", color: "#8F5600", label: "In progress" };
  return { bg: "rgba(112,115,114,0.14)", color: "#707372", label: "Pending" };
}

export function CuttingView({ jobs, onOpenJob }: Props) {
  const eligible = jobs.filter((j) => j.spools.length > 0);
  if (eligible.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 48, color: "var(--bsp-fg-muted)", fontSize: 13 }}>
        No jobs with spools yet.
      </div>
    );
  }
  return (
    <div className="cutting-grid">
      {eligible.map((j) => {
        const done = j.spools.filter(
          (s) => s.status === "cut" || s.status === "welding" || s.status === "complete",
        ).length;
        const tone = badgeTone(done, j.spools.length);
        return (
          <div
            key={j.id}
            className="cut-card"
            onClick={() => onOpenJob(j.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onOpenJob(j.id);
            }}
            style={{ cursor: "pointer" }}
          >
            <div className="cut-card-header">
              <div>
                <span className="proj-id">{j.jobNumber}</span>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--bsp-dark)", marginTop: 5 }}>
                  {j.name}
                </div>
              </div>
              <span className="cut-badge" style={{ background: tone.bg, color: tone.color }}>
                {tone.label}
              </span>
            </div>
            <div>
              {j.spools.map((s) => (
                <div key={s.id} className="cut-spool-row">
                  <div className="cut-status-dot" style={{ background: SPOOL_DOT[s.status] }} />
                  <div style={{ flex: 1, fontFamily: "var(--bsp-font-mono)", fontSize: 11, color: "var(--bsp-dark)", fontWeight: 600 }}>
                    {s.num}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)" }}>
                    {s.description ?? ""}
                  </div>
                  <div style={{ fontFamily: "var(--bsp-font-mono)", fontSize: 11, fontWeight: 700, color: "var(--bsp-blue)", minWidth: 34, textAlign: "right" }}>
                    {s.hours.toFixed(1)}h
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
